<div class="users">

  <!-- Page Header -->
  <ui-page-header [title]="'settings.users' | translate" [lucideIcon]="'Users'">
    <ui-button
      type="primary"
      [label]="(('common.add' | translate) + ' ' + ('user.user' | translate)) | uppercase"
      icon="plus-cc"
      iconSize="large"
      iconPosition="start"
      (click)="openCreateDialog()">
    </ui-button>
  </ui-page-header>

  <!-- Toolbar -->
  <div class="users__toolbar">
    <ui-form-field
      [formControl]="searchControl"
      [suffixIcon]="'search_icon'"
      [placeholder]="'settings.search_name_email' | translate">
    </ui-form-field>

    <ui-form-field
      type="select"
      [formControl]="roleFilterControl"
      [options]="roleOptions">
    </ui-form-field>

    <!-- Active / deleted / all — super-admins only, with an inline note saying so. -->
    <div class="users__status-filter" *ngIf="isSuperAdmin$ | async">
      <ui-form-field
        type="select"
        [formControl]="deletedFilterControl"
        [options]="deletedFilterOptions">
      </ui-form-field>
      <span
        class="users__status-filter-info"
        [matTooltip]="'settings.super_admin_only' | translate"
        matTooltipPosition="above">
        <ui-icon name="Info" size="medium"></ui-icon>
      </span>
    </div>

    <ng-container *ngIf="filteredUsers$ | async as filtered">
      <span class="users__result-count">{{ filtered.length }} {{ 'settings.users' | translate | lowercase }}</span>
    </ng-container>
  </div>

  <!-- Table Card -->
  <div class="users__table-card">
    <div class="users__table-wrapper">
      <table class="users__table">
        <thead>
          <tr class="users__table-head">
            <th class="users__col--avatar-head"></th>
            <th>
              <button class="users__sort-button" (click)="onSort('user')" type="button">
                <span>{{ 'user.user' | translate }}</span>
                <span class="users__sort-arrows">
                  <span class="users__sort-arrow users__sort-arrow--up" [class.users__sort-arrow--active]="sortField === 'user' && sortMode === 'asc'"></span>
                  <span class="users__sort-arrow users__sort-arrow--down" [class.users__sort-arrow--active]="sortField === 'user' && sortMode === 'desc'"></span>
                </span>
              </button>
            </th>
            <th>
              <button class="users__sort-button" (click)="onSort('role')" type="button">
                <span>{{ 'user.roles' | translate }}</span>
                <span class="users__sort-arrows">
                  <span class="users__sort-arrow users__sort-arrow--up" [class.users__sort-arrow--active]="sortField === 'role' && sortMode === 'asc'"></span>
                  <span class="users__sort-arrow users__sort-arrow--down" [class.users__sort-arrow--active]="sortField === 'role' && sortMode === 'desc'"></span>
                </span>
              </button>
            </th>
            <th>
              <button class="users__sort-button" (click)="onSort('status')" type="button">
                <span>{{ 'common.status' | translate }}</span>
                <span class="users__sort-arrows">
                  <span class="users__sort-arrow users__sort-arrow--up" [class.users__sort-arrow--active]="sortField === 'status' && sortMode === 'asc'"></span>
                  <span class="users__sort-arrow users__sort-arrow--down" [class.users__sort-arrow--active]="sortField === 'status' && sortMode === 'desc'"></span>
                </span>
              </button>
            </th>
            <th>
              <button class="users__sort-button" (click)="onSort('last_activity')" type="button">
                <span>{{ 'common.last_activity' | translate }}</span>
                <span class="users__sort-arrows">
                  <span class="users__sort-arrow users__sort-arrow--up" [class.users__sort-arrow--active]="sortField === 'last_activity' && sortMode === 'asc'"></span>
                  <span class="users__sort-arrow users__sort-arrow--down" [class.users__sort-arrow--active]="sortField === 'last_activity' && sortMode === 'desc'"></span>
                </span>
              </button>
            </th>
            <th class="users__col--actions-head"></th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let user of sortedUsers$ | async; trackBy: trackByUserId">
            <tr
              class="users__row"
              [class.users__row--selected]="selectedUser?.id === user.id"
              [class.users__row--inactive]="!user.enabled"
              (click)="selectUser(user)"
            >
              <!-- Avatar -->
              <td class="users__col--avatar">
                <div
                  class="users__avatar"
                  [style.background-color]="!user.image ? getUserColor(user) : 'transparent'"
                >
                  <img *ngIf="user.image" [src]="getImageSrc(user.image)" [alt]="user.name" class="users__avatar-img" />
                  <span *ngIf="!user.image" class="users__avatar-initials">{{ getUserInitials(user) }}</span>
                </div>
              </td>

              <!-- User Info -->
              <td class="users__col--user">
                <span class="users__user-name-row">
                  <span class="users__user-name">{{ user.name }}</span>
                  <span *ngIf="user.email === currentUserEmail" class="users__you-badge">you</span>
                </span>
                <span class="users__user-email">{{ user.email }}</span>
              </td>

              <!-- Role -->
              <td class="users__col--role">
                <div class="users__role-cell" *ngIf="getUserRole(user) as role; else noRoleCell">
                  <span class="users__role-dot" [style.background-color]="getRoleColor(role)"></span>
                  <span class="users__role-label">{{ getRoleLabel(role.name) }}</span>
                  <mat-icon class="users__role-protected-icon" *ngIf="role.is_protected">lock_outline</mat-icon>
                </div>
                <ng-template #noRoleCell>
                  <span class="users__empty">{{ emptyStateValue }}</span>
                </ng-template>
              </td>

              <!-- Status -->
              <td class="users__col--status">
                <span
                  class="users__status-badge"
                  [class.users__status-badge--active]="user.enabled"
                  [class.users__status-badge--inactive]="!user.enabled"
                >
                  {{ getUserStatusLabel(user.enabled) }}
                </span>
              </td>

              <!-- Last Activity -->
              <td class="users__col--activity">
                <span class="users__activity">{{ getLastActivity(user) }}</span>
              </td>

              <!-- Row Actions -->
              <td class="users__col--actions" (click)="$event.stopPropagation()">
                <button
                  *ngIf="!user.is_super_admin"
                  mat-icon-button
                  [matMenuTriggerFor]="rowMenu"
                  class="users__actions-btn"
                  [attr.aria-label]="'common.actions' | translate"
                >
                  <mat-icon>more_horiz</mat-icon>
                </button>
                <mat-menu #rowMenu="matMenu" xPosition="before" panelClass="ui-action-menu">
                  <ng-container *ngIf="!user.is_deleted">
                    <button mat-menu-item class="ui-action-menu-item" (click)="openEditDialog(user)">
                      <span class="ui-action-menu-item__icon">
                        <ui-svg-icon svgIcon="pen-cc"></ui-svg-icon>
                      </span>
                      <span class="ui-action-menu-item__label">{{ 'common.edit' | translate }}</span>
                    </button>
                    <button mat-menu-item class="ui-action-menu-item" (click)="toggleEnabled(user)">
                      <span class="ui-action-menu-item__icon">
                        <ui-svg-icon [svgIcon]="user.enabled ? 'user-inactive' : 'user-active'"></ui-svg-icon>
                      </span>
                      <span class="ui-action-menu-item__label">{{ getUserToggleActionLabel(user.enabled) }}</span>
                    </button>
                    <!-- The permission POST /users/:id/mfa/reset enforces. -->
                    <button
                      *appHasPermission="{ permission: 'edit:settings_security' }"
                      mat-menu-item
                      class="ui-action-menu-item"
                      (click)="resetUserMfa(user)"
                    >
                      <span class="ui-action-menu-item__icon">
                        <!-- pxSize to match the sibling `ui-svg-icon` items,
                             whose default size="large" is 24px here. -->
                        <ui-icon name="ShieldCheck" [pxSize]="24"></ui-icon>
                      </span>
                      <span class="ui-action-menu-item__label">
                        {{ 'settings_security.reset_short' | translate }}
                      </span>
                    </button>
                    <button mat-menu-item class="ui-action-menu-item ui-action-menu-item--danger" (click)="deleteUser(user)">
                      <span class="ui-action-menu-item__icon">
                        <ui-svg-icon svgIcon="trash-cc"></ui-svg-icon>
                      </span>
                      <span class="ui-action-menu-item__label">{{ 'common.delete' | translate }}</span>
                    </button>
                  </ng-container>
                  <ng-container *ngIf="user.is_deleted">
                    <button mat-menu-item class="ui-action-menu-item" (click)="restoreUser(user)">
                      <span class="ui-action-menu-item__icon">
                        <ui-svg-icon svgIcon="user-active"></ui-svg-icon>
                      </span>
                      <span class="ui-action-menu-item__label">{{ 'common.restore' | translate }}</span>
                    </button>
                  </ng-container>
                </mat-menu>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <app-pagination
      [pagination]="metadata$ | async"
      (toPageEvent)="onPageChange($event)">
    </app-pagination>
  </div>
</div>
